import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { usePageMeta } from "../../lib/usePageMeta";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";

const PLAN_NAMES = { team: "Team", business: "Business", energia: "Energia" };

export default function Signup() {
  usePageMeta({
    title: "Crea il tuo account | Wattline",
    description: "Registra la tua azienda su Wattline in due minuti e attiva la prova gratuita di 7 giorni, senza carta di credito.",
  });
  const [params] = useSearchParams();
  const [plan, setPlan] = useState(PLAN_NAMES[params.get("plan")] ? params.get("plan") : "team");
  const interval = "yearly";
  const [form, setForm] = useState({ company_name: "", vat_number: "", first_name: "", last_name: "", email: "", password: "" });
  const [collabs, setCollabs] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [emailSent, setEmailSent] = useState(true);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const collaborator_emails = collabs
        .split(/[\n,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
      const { data } = await api.post("/public/signup", {
        ...form, collaborator_emails, plan, interval, origin_url: window.location.origin,
      });
      setDone(true);
      setEmailSent(Boolean(data.email_sent));
      if (!data.email_sent) toast.warning("Registrazione ok ma invio email non riuscito: usa 'Invia di nuovo'.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Registrazione non riuscita");
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/public/resend-verification", { email: form.email, origin_url: window.location.origin });
      toast.success("Email inviata di nuovo. Controlla la posta.");
    } catch {
      toast.error("Invio non riuscito, riprova tra poco.");
    }
  };

  return (
    <div className="min-h-screen bg-page flex flex-col" data-testid="signup-page">
      <header className="flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/plans" className="text-lg font-semibold text-text-primary tracking-tight">Wattline</Link>
        <Link to="/login" className="wl-btn wl-btn--ghost">Accedi</Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 pb-12">
        <div className="w-full max-w-md bg-surface border border-border rounded-2xl p-7">
          {done ? (
            <div className="text-center py-6" data-testid="signup-success">
              <MailCheck size={40} className={`mx-auto mb-4 ${emailSent ? "text-accent" : "text-amber-500"}`} />
              <h2 className="text-lg font-semibold text-text-primary">
                {emailSent ? "Controlla la tua email" : "Email non inviata"}
              </h2>
              <p className="text-sm text-text-secondary mt-2">
                {emailSent ? (
                  <>Ti abbiamo inviato un link a <strong>{form.email}</strong>: confermalo per
                  iniziare la prova gratuita di 7 giorni del piano {PLAN_NAMES[plan]}.</>
                ) : (
                  <>Il tuo account è stato creato ma non siamo riusciti a inviare l'email di
                  conferma a <strong>{form.email}</strong>. Riprova con «Invia di nuovo» tra qualche istante.</>
                )}
              </p>
              <button type="button" onClick={resend} data-testid="signup-resend"
                      className="wl-btn wl-btn--ghost mt-5" style={{ border: "1px solid var(--wl-border)" }}>
                Invia di nuovo
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-text-primary">Crea il tuo account</h1>
              <p className="text-sm text-text-secondary mt-1">
                Prova gratuita di 7 giorni · senza carta · piano <strong>{PLAN_NAMES[plan]}</strong> · fatturazione annuale
              </p>
              <div className="flex gap-2 mt-3">
                {Object.entries(PLAN_NAMES).map(([id, name]) => (
                  <button key={id} type="button" onClick={() => setPlan(id)} data-testid={`signup-plan-${id}`}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${plan === id ? "border-accent text-text-primary bg-accent-subtle" : "border-border text-text-secondary"}`}>
                    {name}
                  </button>
                ))}
              </div>
              <form onSubmit={submit} className="space-y-3 mt-5">
                <input className="wl-input w-full" placeholder="Nome azienda" required minLength={2} maxLength={80}
                       value={form.company_name} onChange={set("company_name")} data-testid="signup-company" />
                <input className="wl-input w-full" placeholder="Partita IVA (11 cifre)" required minLength={8} maxLength={20}
                       value={form.vat_number} onChange={set("vat_number")} data-testid="signup-vat" />
                <div className="grid grid-cols-2 gap-3">
                  <input className="wl-input w-full" placeholder="Nome" required maxLength={60}
                         value={form.first_name} onChange={set("first_name")} data-testid="signup-firstname" />
                  <input className="wl-input w-full" placeholder="Cognome" required maxLength={60}
                         value={form.last_name} onChange={set("last_name")} data-testid="signup-lastname" />
                </div>
                <input className="wl-input w-full" type="email" placeholder="Email di lavoro" required
                       value={form.email} onChange={set("email")} data-testid="signup-email" />
                <input className="wl-input w-full" type="password" placeholder="Password (min 8 caratteri)" required minLength={8}
                       value={form.password} onChange={set("password")} data-testid="signup-password" />
                <div>
                  <textarea className="wl-textarea w-full" rows={2}
                            placeholder="Email dei collaboratori (facoltativo, una per riga o separate da virgola)"
                            value={collabs} onChange={(e) => setCollabs(e.target.value)}
                            data-testid="signup-collaborators" />
                  <p className="text-[11px] text-text-muted mt-1">
                    Riceveranno l'invito con le credenziali appena confermi la tua email.
                  </p>
                </div>
                <button type="submit" disabled={submitting} data-testid="signup-submit"
                        className="wl-btn wl-btn--primary w-full">
                  {submitting ? <Loader2 size={15} className="animate-spin" /> : null}
                  {submitting ? "Creazione account…" : "Inizia la prova gratuita"}
                </button>
              </form>
              <p className="text-[11px] text-text-muted mt-4 text-center">
                Registrandoti accetti i termini del servizio. Nessuna carta richiesta per la prova.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
